// ============================================================
// Advertise Me — main app + router + nav + footer + tweaks
// ============================================================
import React, { useState as useS, useEffect as useE, useMemo as useM, useRef as useR } from 'react';
import { useTweaks, TweaksPanel, TweakSection, TweakColor, TweakRadio, TweakSelect } from './tweaks-panel.jsx';
import { BrandMark, ScrollScene, ScrollHUD } from './components.jsx';
import { PortfolioPage, HomePage, ProductPage, IndustriesPage, IndustryPage, AboutPage, QuotePage, PricingPage, DemosPage } from './pages.jsx';
import { AM_DATA } from './data.js';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#0066ff",
  "heroMode": "morph",
  "typography": "geometric",
  "accentStyle": "color",
  "variant": "studio"
}/*EDITMODE-END*/;

// ---- Router (pathname-based) ----
function normaliseRoute(p) {
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

function useRoute() {
  const [pathname, setPathname] = useS(() => typeof window !== 'undefined' ? normaliseRoute(window.location.pathname) : '/');
  useE(() => {
    const onNav = () => {
      setPathname(normaliseRoute(window.location.pathname));
      if(window.gtag) window.gtag("config", "G-Y1F9S226RJ", { page_path: window.location.pathname });
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    window.addEventListener('popstate', onNav);
    window.addEventListener('pushstate', onNav);
    return () => {
      window.removeEventListener('popstate', onNav);
      window.removeEventListener('pushstate', onNav);
    };
  }, []);
  return pathname;
}

function navigate(to) {
  history.pushState(null, '', to);
  window.dispatchEvent(new Event('pushstate'));
}

// ---- Nav ----
function Nav({ route }) {
  const [scrolled, setScrolled] = useS(false);
  const [menuOpen, setMenuOpen] = useS(false);
  const [dropOpen, setDropOpen] = useS(false);
  const dropRef = useR(null);
  const dropTimeout = useR(null);
  const [indDropOpen, setIndDropOpen] = useS(false);
  const indDropRef = useR(null);
  const indDropTimeout = useR(null);

  const [demoDropOpen, setDemoDropOpen] = useS(false);
  const demoDropRef = useR(null);
  const demoDropTimeout = useR(null);

  useE(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useE(() => { setMenuOpen(false); setDropOpen(false); setIndDropOpen(false); setDemoDropOpen(false); }, [route]);

  // Close dropdown on outside click
  useE(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (indDropRef.current && !indDropRef.current.contains(e.target)) setIndDropOpen(false);
      if (demoDropRef.current && !demoDropRef.current.contains(e.target)) setDemoDropOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleDropEnter = () => { clearTimeout(dropTimeout.current); setDropOpen(true); };
  const handleDropLeave = () => { dropTimeout.current = setTimeout(() => setDropOpen(false), 200); };
  const handleIndDropEnter = () => { clearTimeout(indDropTimeout.current); setIndDropOpen(true); };
  const handleIndDropLeave = () => { indDropTimeout.current = setTimeout(() => setIndDropOpen(false), 200); };
  const handleDemoDropEnter = () => { clearTimeout(demoDropTimeout.current); setDemoDropOpen(true); };
  const handleDemoDropLeave = () => { demoDropTimeout.current = setTimeout(() => setDemoDropOpen(false), 200); };

  const isProductRoute = route.startsWith('/products/');
  const isIndustryRoute = route.startsWith('/industries/');
  const isDemoRoute = route.startsWith('/demos');

  const products = AM_DATA.products;
  const industries = AM_DATA.industryPages || [];

  // Group products by category for dropdown
  const itProducts = products.filter(p => p.slug.startsWith('interactive-touch/'));
  const dsProducts = products.filter(p => p.slug.startsWith('digital-signage/'));

  const demos = [
    { slug: 'interactive-timetable-board', name: 'Timetable Board', desc: 'Real-time class schedules for universities and schools' },
    { slug: 'interactive-expo-schedule-board', name: 'Expo Schedule Board', desc: 'Event programmes with speakers, sponsors and QR codes' },
    { slug: 'interactive-directory-board', name: 'Directory Board', desc: 'Corporate tenant directory with live status and inline editor' },
    { slug: 'interactive-eposter-board', name: 'ePoster Gallery', desc: 'Browse, zoom and rate research posters at conferences and events' },
    { slug: 'interactive-expo-directory-board', name: 'Expo Directory Board', desc: 'Touchscreen exhibitor directory with live wayfinding for trade shows and expos' },
  ];

  return (
    React.createElement(React.Fragment, null,
      React.createElement('nav', { className: `nav ${scrolled ? 'scrolled' : ''}` },
        React.createElement('div', { className: 'nav-inner' },
          React.createElement('a', { 'data-link': true, href: '/', className: 'nav-brand', style: {cursor:'pointer',display:'flex',alignItems:'center'} },
            React.createElement('img', { src: '/images/logo-landscape.png', alt: 'Advertise Me', style: {height:36,width:'auto',display:'block'} })
          ),
          React.createElement('div', { className: 'nav-links', style: {gap:10} },

            // Products dropdown — grouped by category
            React.createElement('div', { className: 'nav-dropdown-wrap', ref: dropRef, onMouseEnter: handleDropEnter, onMouseLeave: handleDropLeave },
              React.createElement('button', {
                className: `nav-link nav-link--drop ${isProductRoute ? 'active' : ''}`,
                onClick: () => setDropOpen(o => !o),
                'aria-expanded': dropOpen,
                'aria-haspopup': 'true'
              },
                'Products',
                React.createElement('svg', { className: `nav-drop-chevron ${dropOpen ? 'open' : ''}`, width: '10', height: '10', viewBox: '0 0 10 10' },
                  React.createElement('path', { d: 'M2 4L5 7L8 4', stroke: 'currentColor', strokeWidth: '1.4', fill: 'none', strokeLinecap: 'round' })
                )
              ),
              React.createElement('div', { className: `nav-dropdown ${dropOpen ? 'open' : ''}` },
                React.createElement('div', { className: 'nav-dropdown-inner' },
                  React.createElement('div', { className: 'nav-dropdown-category' }, 'Interactive Touch'),
                  itProducts.map((p) =>
                    React.createElement('a', {
                      key: p.slug,
                      'data-link': true,
                      href: `/products/${p.slug}`,
                      className: `nav-dropdown-item ${route === '/products/' + p.slug ? 'active' : ''}`
                    },
                      React.createElement('span', { className: 'nav-dropdown-item-name' }, p.name),
                      React.createElement('span', { className: 'nav-dropdown-item-desc' }, p.summary)
                    )
                  ),
                  React.createElement('div', { className: 'nav-dropdown-category' }, 'Digital Signage'),
                  dsProducts.map((p) =>
                    React.createElement('a', {
                      key: p.slug,
                      'data-link': true,
                      href: `/products/${p.slug}`,
                      className: `nav-dropdown-item ${route === '/products/' + p.slug ? 'active' : ''}`
                    },
                      React.createElement('span', { className: 'nav-dropdown-item-name' }, p.name),
                      React.createElement('span', { className: 'nav-dropdown-item-desc' }, p.summary)
                    )
                  ),
                  React.createElement('div', { className: 'nav-dropdown-footer' },
                    React.createElement('a', { 'data-link': true, href: '/pricing', className: 'nav-dropdown-footer-link' },
                      'How pricing works',
                      React.createElement('svg', { width: '12', height: '12', viewBox: '0 0 16 16' },
                        React.createElement('path', { d: 'M3 8H13M8 3L13 8L8 13', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none', strokeLinecap: 'round' })
                      )
                    )
                  )
                )
              )
            ),

            // Demos dropdown
            React.createElement('div', { className: 'nav-dropdown-wrap', ref: demoDropRef, onMouseEnter: handleDemoDropEnter, onMouseLeave: handleDemoDropLeave },
              React.createElement('button', {
                className: `nav-link nav-link--drop ${isDemoRoute ? 'active' : ''}`,
                onClick: () => setDemoDropOpen(o => !o),
                'aria-expanded': demoDropOpen,
                'aria-haspopup': 'true'
              },
                'Demos',
                React.createElement('svg', { className: `nav-drop-chevron ${demoDropOpen ? 'open' : ''}`, width: '10', height: '10', viewBox: '0 0 10 10' },
                  React.createElement('path', { d: 'M2 4L5 7L8 4', stroke: 'currentColor', strokeWidth: '1.4', fill: 'none', strokeLinecap: 'round' })
                )
              ),
              React.createElement('div', { className: `nav-dropdown ${demoDropOpen ? 'open' : ''}` },
                React.createElement('div', { className: 'nav-dropdown-inner' },
                  demos.map((d) =>
                    React.createElement('a', {
                      key: d.slug,
                      'data-link': true,
                      href: `/demos/${d.slug}`,
                      className: `nav-dropdown-item ${route === '/demos/' + d.slug ? 'active' : ''}`
                    },
                      React.createElement('span', { className: 'nav-dropdown-item-name' }, d.name),
                      React.createElement('span', { className: 'nav-dropdown-item-desc' }, d.desc)
                    )
                  ),
                  React.createElement('div', { className: 'nav-dropdown-footer' },
                    React.createElement('a', { 'data-link': true, href: '/demos', className: 'nav-dropdown-footer-link' },
                      'View all demos',
                      React.createElement('svg', { width: '12', height: '12', viewBox: '0 0 16 16' },
                        React.createElement('path', { d: 'M3 8H13M8 3L13 8L8 13', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none', strokeLinecap: 'round' })
                      )
                    )
                  )
                )
              )
            ),

            // Industries dropdown
            React.createElement('div', { className: 'nav-dropdown-wrap', ref: indDropRef, onMouseEnter: handleIndDropEnter, onMouseLeave: handleIndDropLeave },
              React.createElement('button', {
                className: `nav-link nav-link--drop ${isIndustryRoute ? 'active' : ''}`,
                onClick: () => setIndDropOpen(o => !o),
                'aria-expanded': indDropOpen,
                'aria-haspopup': 'true'
              },
                'Industries',
                React.createElement('svg', { className: `nav-drop-chevron ${indDropOpen ? 'open' : ''}`, width: '10', height: '10', viewBox: '0 0 10 10' },
                  React.createElement('path', { d: 'M2 4L5 7L8 4', stroke: 'currentColor', strokeWidth: '1.4', fill: 'none', strokeLinecap: 'round' })
                )
              ),
              React.createElement('div', { className: `nav-dropdown ${indDropOpen ? 'open' : ''}` },
                React.createElement('div', { className: 'nav-dropdown-inner' },
                  industries.map((ind) =>
                    React.createElement('a', {
                      key: ind.slug,
                      'data-link': true,
                      href: `/industries/${ind.slug}`,
                      className: `nav-dropdown-item ${route === '/industries/' + ind.slug ? 'active' : ''}`
                    },
                      React.createElement('span', { className: 'nav-dropdown-item-name' }, ind.name),
                      React.createElement('span', { className: 'nav-dropdown-item-desc' }, ind.short)
                    )
                  ),
                  React.createElement('div', { className: 'nav-dropdown-footer' },
                    React.createElement('a', { 'data-link': true, href: '/industries', className: 'nav-dropdown-footer-link' },
                      'View all industries',
                      React.createElement('svg', { width: '12', height: '12', viewBox: '0 0 16 16' },
                        React.createElement('path', { d: 'M3 8H13M8 3L13 8L8 13', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none', strokeLinecap: 'round' })
                      )
                    )
                  )
                )
              )
            ),

            React.createElement('a', { 'data-link': true, href: '/portfolio', className: `nav-link ${route === '/portfolio' ? 'active' : ''}` }, 'Portfolio'),
            React.createElement('a', { 'data-link': true, href: '/pricing', className: `nav-link ${route === '/pricing' ? 'active' : ''}` }, 'Pricing'),
            React.createElement('a', { 'data-link': true, href: '/about', className: `nav-link ${route === '/about' ? 'active' : ''}` }, 'About'),
            React.createElement('a', { 'data-link': true, href: '/quote', className: `nav-link ${route === '/quote' ? 'active' : ''}` }, 'Contact')
          ),
          React.createElement('a', { 'data-link': true, href: '/quote', className: 'btn nav-cta' },
            'Get a quote',
            React.createElement('span', { className: 'arrow' },
              React.createElement('svg', { viewBox: '0 0 16 16', width: '14', height: '14' },
                React.createElement('path', { d: 'M4 12 L12 4 M6 4 H12 V10', stroke: 'currentColor', strokeWidth: '1.4', fill: 'none', strokeLinecap: 'round' })
              )
            )
          ),
          React.createElement('button', { className: 'nav-burger', onClick: () => setMenuOpen(o => !o), 'aria-label': 'menu' },
            React.createElement('span', null)
          )
        )
      ),

      // Mobile drawer
      React.createElement('div', { className: `menu-drawer ${menuOpen ? 'open' : ''}` },
        React.createElement('a', { 'data-link': true, href: '/' }, 'Home'),
        React.createElement('div', { className: 'menu-drawer-section' }, 'Interactive Touch'),
        itProducts.map((p) =>
          React.createElement('a', { key: p.slug, 'data-link': true, href: `/products/${p.slug}`, className: 'menu-drawer-sub' }, p.name)
        ),
        React.createElement('div', { className: 'menu-drawer-section' }, 'Digital Signage'),
        dsProducts.map((p) =>
          React.createElement('a', { key: p.slug, 'data-link': true, href: `/products/${p.slug}`, className: 'menu-drawer-sub' }, p.name)
        ),
        React.createElement('div', { className: 'menu-drawer-section' }, 'Demos'),
        demos.map((d) =>
          React.createElement('a', { key: d.slug, 'data-link': true, href: `/demos/${d.slug}`, className: 'menu-drawer-sub' }, d.name)
        ),
        React.createElement('div', { className: 'menu-drawer-section' }, 'Industries'),
        industries.map((ind) =>
          React.createElement('a', { key: ind.slug, 'data-link': true, href: `/industries/${ind.slug}`, className: 'menu-drawer-sub' }, ind.name)
        ),
        React.createElement('a', { 'data-link': true, href: '/portfolio' }, 'Portfolio'),
        React.createElement('a', { 'data-link': true, href: '/pricing' }, 'Pricing'),
        React.createElement('a', { 'data-link': true, href: '/about' }, 'About'),
        React.createElement('a', { 'data-link': true, href: '/quote', className: 'btn btn--accent' }, 'Get a quote')
      )
    )
  );
}

// ---- Footer ----
function Footer() {
  return (
    React.createElement('footer', { className: 'footer' },
      React.createElement('div', { className: 'container' },
        React.createElement('div', { className: 'grid' },
          React.createElement('div', { style: {display:'flex',flexDirection:'column',alignItems:'center'} },
            React.createElement('div', { style: {display:'flex',justifyContent:'center',marginBottom:8} },
              React.createElement('img', { src: '/images/logo-square.png', alt: 'Advertise Me', style: {height:100,width:'auto',display:'block'} })
            ),
            React.createElement('p', { className: 'body', style: {marginTop:8,color:'rgba(255,255,255,0.6)',maxWidth:'36ch',textAlign:'center'} },
              'Custom digital signage and interactive solutions, designed and built in Australia.'
            )
          ),
          React.createElement('div', { className: 'col-list' },
            React.createElement('h4', null, 'Interactive Touch'),
            React.createElement('a', { 'data-link': true, href: '/products/interactive-touch/digital-wayfinding' }, 'Digital Wayfinding'),
            React.createElement('a', { 'data-link': true, href: '/products/interactive-touch/information-kiosks' }, 'Information Kiosks'),
            React.createElement('a', { 'data-link': true, href: '/products/interactive-touch/eposters' }, 'ePosters'),
            React.createElement('a', { 'data-link': true, href: '/products/interactive-touch/directory-boards' }, 'Interactive Directory Boards')
          ),
          React.createElement('div', { className: 'col-list' },
            React.createElement('h4', null, 'Digital Signage'),
            React.createElement('a', { 'data-link': true, href: '/products/digital-signage/medical-centres' }, 'Medical Centre Signage'),
            React.createElement('a', { 'data-link': true, href: '/products/digital-signage/bed-management' }, 'Bed Management Display'),
            React.createElement('a', { 'data-link': true, href: '/products/digital-signage/directory-boards' }, 'Digital Directory Boards'),
            React.createElement('a', { 'data-link': true, href: '/products/digital-signage/custom' }, 'Custom Digital Signage')
          ),
          React.createElement('div', { className: 'col-list' },
            React.createElement('h4', null, 'Get in touch'),
            React.createElement('a', { href: 'tel:1300880005' }, '1300 880 005'),
            React.createElement('a', { href: 'mailto:hello@advertiseme.au' }, 'hello@advertiseme.au'),
            React.createElement('a', { href: '#' }, 'Sydney, NSW, Australia')
          ),
          React.createElement('div', { className: 'col-list' },
            React.createElement('h4', null, 'Company'),
            React.createElement('a', { 'data-link': true, href: '/pricing' }, 'Pricing'),
            React.createElement('a', { 'data-link': true, href: '/industries' }, 'Industries'),
            React.createElement('a', { 'data-link': true, href: '/about' }, 'About'),
            React.createElement('a', { 'data-link': true, href: '/quote' }, 'Contact')
          )
        ),
        React.createElement('div', { className: 'meta' },
          React.createElement('span', null, `© ${new Date().getFullYear()} Advertise Me Pty. Ltd. · ABN 51 116 452 058`),
          React.createElement('span', null, 'v.26.06 · all systems normal ◉')
        )
      )
    )
  );
}

// ---- Tweaks panel ----
function AppTweaks({ tweaks, set }) {
  if (!TweaksPanel) return null;
  return (
    React.createElement(TweaksPanel, { title: 'Tweaks' },
      React.createElement(TweakSection, { label: 'Variant' },
        React.createElement(TweakRadio, {
          label: 'Direction',
          value: tweaks.variant,
          options: [{ label: 'Studio', value: 'studio' }, { label: 'Live', value: 'live' }],
          onChange: (v) => set('variant', v)
        })
      ),
      React.createElement(TweakSection, { label: 'Accent color' },
        React.createElement(TweakColor, {
          label: 'Accent',
          value: tweaks.accent,
          options: ['#0066ff', '#0a0a0a', '#00a86b', '#ff5a1f', '#7c3aed', '#e11d48'],
          onChange: (v) => set('accent', v)
        })
      ),
      React.createElement(TweakSection, { label: 'Hero animation' },
        React.createElement(TweakSelect, {
          label: 'Style',
          value: tweaks.heroMode,
          options: [
            { label: 'Morph — drifting accent shapes', value: 'morph' },
            { label: 'Grid — animated dot wave', value: 'grid' },
            { label: 'Wordmark — kinetic typography', value: 'wordmark' }
          ],
          onChange: (v) => set('heroMode', v)
        })
      ),
      React.createElement(TweakSection, { label: 'Accent text style' },
        React.createElement(TweakSelect, {
          label: 'For emphasised words',
          value: tweaks.accentStyle,
          options: [
            { label: 'Color only — same font, accent color', value: 'color' },
            { label: 'Italic light — Inter italic 300', value: 'italic' },
            { label: 'Mono italic — JetBrains Mono', value: 'mono' },
            { label: 'Underline — accent underline', value: 'underline' },
            { label: 'Editorial — Newsreader italic', value: 'editorial' }
          ],
          onChange: (v) => set('accentStyle', v)
        })
      ),
      React.createElement(TweakSection, { label: 'Typography' },
        React.createElement(TweakSelect, {
          label: 'Type family',
          value: tweaks.typography,
          options: [
            { label: 'Geometric — Inter', value: 'geometric' },
            { label: 'Grotesque — Space Grotesk', value: 'grotesque' }
          ],
          onChange: (v) => set('typography', v)
        })
      )
    )
  );
}

// ---- Page titles ----
const PAGE_META = {
  '/': { title: 'Advertise Me — Digital Signage & Interactive Solutions', desc: 'Custom digital signage, interactive touch solutions, digital wayfinding and directory boards for medical centres, shopping centres and hospitals. Designed, built and supported in Sydney, Australia.' },
  '/industries': { title: 'Industries We Serve — Advertise Me', desc: 'Digital signage solutions for medical centres, shopping centres, universities and hospitals across Australia.' },
  '/about': { title: 'About Advertise Me — Digital Signage Experts Since 2009', desc: 'Australian-owned digital signage company based in Sydney. 17 years building custom interactive solutions.' },
  '/quote': { title: 'Get a Quote — Advertise Me', desc: 'Request a custom quote for digital signage, touch solutions, wayfinding or directory boards. 48-hour turnaround.' },
  '/portfolio': { title: 'Portfolio | Advertise Me', desc: 'Explore our portfolio of digital signage, interactive kiosks, wayfinding systems and ePoster displays deployed across Australia.' },
  '/pricing': { title: 'How Pricing Works — Advertise Me', desc: 'Transparent pricing for digital signage, touch solutions and interactive displays. Hardware, software and professional services scoped to your requirements.' },
  '/products/interactive-touch': { title: 'Interactive Touch Solutions — Advertise Me', desc: 'Digital wayfinding kiosks, information kiosks, ePosters and interactive directory boards. Touchscreen solutions built for public spaces across Australia.' },
  '/products/interactive-touch/digital-wayfinding': { title: 'Digital Wayfinding — Advertise Me', desc: 'Interactive wayfinding kiosks and touchscreens that guide visitors from entry to destination. Deployed in hospitals, universities and shopping centres across Australia.' },
  '/products/interactive-touch/information-kiosks': { title: 'Information Kiosks — Advertise Me', desc: 'Cloud-managed touchscreen kiosks and wall-mounted displays for self-service, directories and visitor information. Built for foyers, receptions and public spaces.' },
  '/products/interactive-touch/eposters': { title: 'ePosters — Advertise Me', desc: 'Interactive digital posters for universities and research centres. Touch-first, video-rich, analytics-enabled.' },
  '/products/interactive-touch/directory-boards': { title: 'Interactive Directory Boards — Advertise Me', desc: 'Touch-enabled digital directory boards for foyers. Visitors tap to find people, rooms and services instantly.' },
  '/products/digital-signage': { title: 'Digital Signage — Advertise Me', desc: 'Purpose-built digital signage for medical centres, hospitals and commercial environments. Cloud-managed, always current, always on.' },
  '/products/digital-signage/medical-centres': { title: 'Digital Signage for Medical Centres — Advertise Me', desc: 'Staff rosters, in/out boards and patient queue displays synced to your practice management system.' },
  '/products/digital-signage/bed-management': { title: 'Bed Management Display — Advertise Me', desc: 'Live patient flow and bed availability across wards. Streamline assignments and clinician handover.' },
  '/products/digital-signage/directory-boards': { title: 'Digital Directory Boards — Advertise Me', desc: 'Always-current passive digital directory boards for foyers and receptions. Updated instantly from a browser.' },
  '/products/digital-signage/custom': { title: 'Custom Digital Signage — Advertise Me', desc: 'Bespoke digital signage for any screen, any content, any environment. Carpark displays, wayfinding maps, internal comms and more.' },
  '/industries/hospitals': { title: 'Digital Signage for Hospitals — Advertise Me', desc: 'Bed management displays, hospital wayfinding kiosks, ward boards and family waiting screens. HL7/FHIR ready, deployed in 12 Australian hospitals.' },
  '/industries/medical-centres': { title: 'Digital Signage for Medical Centres — Advertise Me', desc: 'Live staff roster boards, IN/OUT displays and patient comms for GP clinics and practices. 120+ medical centres live across Australia.' },
  '/industries/education-research': { title: 'Digital Signage for Universities & Research — Advertise Me', desc: 'ePosters, campus wayfinding kiosks and digital signage for universities, TAFEs, colleges and research centres across Australia.' },
  '/industries/shopping-centres': { title: 'Wayfinding & Digital Directories for Shopping Centres — Advertise Me', desc: 'Interactive wayfinding kiosks, tenant directory boards and promotional screens for Australian shopping centres and retail environments.' },
  '/demos': { title: 'Interactive Demos — Advertise Me', desc: 'Explore fully interactive demos of our digital signage, directory boards and expo schedule boards. See our solutions in action.' },
  '/demos/interactive-timetable-board': { title: 'Timetable Board Demo — Advertise Me', desc: 'Interactive timetable board demo for universities and schools. Real-time class schedules with tap-to-expand detail panels.' },
  '/demos/interactive-expo-schedule-board': { title: 'Expo Schedule Demo — Advertise Me', desc: 'Interactive expo schedule board demo with keynotes, speakers, product demos and sponsor banners.' },
  '/demos/interactive-eposter-board': { title: 'ePoster Gallery Demo | Advertise Me', desc: 'Interactive ePoster gallery demo for conferences and research events. Browse, zoom and rate posters in flexible grid layouts.' },
  '/demos/interactive-directory-board': { title: 'Directory Board Demo — Advertise Me', desc: 'Interactive corporate directory board demo with live tenant status, inline editor and light/dark theme switching.' },
  '/demos/interactive-expo-directory-board': { title: 'Expo Directory Board Demo — Advertise Me', desc: 'Interactive expo exhibitor directory demo with live wayfinding, tiered sponsor listings, QR codes and a hidden theme editor.' },
};

function usePageMeta(route) {
  useE(() => {
    const meta = PAGE_META[route] || PAGE_META['/'];
    document.title = meta.title;
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', meta.desc);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.desc);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://advertiseme.com.au' + route);
  }, [route]);
}

function Router({ route, heroMode }) {
  usePageMeta(route);
  if (route === '/' || route === '') return React.createElement(HomePage, { navigate, heroMode });
  if (route === '/industries') return React.createElement(IndustriesPage, { navigate });
  if (route === '/portfolio') return React.createElement(PortfolioPage, null);
  if (route === '/about') return React.createElement(AboutPage, null);
  if (route === '/quote') return React.createElement(QuotePage, null);
  if (route === '/pricing') return React.createElement(PricingPage, { navigate });

  // Product routes — matches /products/category/product
  const mProduct = route.match(/^\/products\/(interactive-touch|digital-signage)\/([\w-]+)\/?$/);
  if (mProduct) return React.createElement(ProductPage, { slug: mProduct[1] + '/' + mProduct[2], navigate });

  // Category landing pages
  if (route === '/products/interactive-touch') return React.createElement(ProductPage, { slug: 'interactive-touch', navigate });
  if (route === '/products/digital-signage') return React.createElement(ProductPage, { slug: 'digital-signage', navigate });

  const mi = route.match(/^\/industries\/([\w-]+)\/?$/);
  if (mi) return React.createElement(IndustryPage, { slug: mi[1], navigate });
  if (route === '/demos') return React.createElement(DemosPage, { navigate });
  const md = route.match(/^\/demos\/([\w-]+)\/?$/);
  if (md) return React.createElement(DemosPage, { slug: md[1], navigate });
  return React.createElement(HomePage, { navigate, heroMode });
}

// ---- App ----
function App({ ssrPath }) {
  const route = ssrPath || useRoute();
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);

  useE(() => {
    document.documentElement.style.setProperty('--accent', tweaks.accent);
    document.documentElement.setAttribute('data-variant', tweaks.variant);
    document.documentElement.setAttribute('data-type', tweaks.typography);
    document.documentElement.setAttribute('data-accent-style', tweaks.accentStyle);
  }, [tweaks.accent, tweaks.variant, tweaks.typography, tweaks.accentStyle]);

  useE(() => {
    const onClick = (e) => {
      const link = e.target.closest('[data-link]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) return;
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    React.createElement(React.Fragment, null,
      React.createElement(ScrollScene, null),
      React.createElement(Nav, { route }),
      React.createElement(Router, { route, heroMode: tweaks.heroMode }),
      route === '/' && React.createElement(ScrollHUD, null),
      React.createElement(Footer, null),
      React.createElement(AppTweaks, { tweaks, set: setTweak })
    )
  );
}

export { App, navigate };
